import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, inject, input } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import type { MitComercialMetadata } from '../../core/models';

export type MitComercialMetadataType = 'TEXT' | 'NUMBER';
export type MitComercialMetadataForm = FormArray<FormGroup>;

/** Mismas reglas que valida la API. */
export const MIT_COMERCIAL_METADATA_MAX_KEYS = 20;
export const MIT_COMERCIAL_METADATA_RESERVED_KEYS: readonly string[] = ['mitTransactionId', 'mitMandateId', 'trackingId'];
const MAX_KEY_LENGTH = 40;
const MAX_TEXT_LENGTH = 500;
const INT32_MIN = -2147483648;
const INT32_MAX = 2147483647;

export function mitComercialMetadataKeyError(key: string | null | undefined): 'required' | 'maxlength' | 'reserved' | null {
  const value = (key ?? '').trim();
  if (!value) return 'required';
  if (value.length > MAX_KEY_LENGTH) return 'maxlength';
  if (MIT_COMERCIAL_METADATA_RESERVED_KEYS.includes(value)) return 'reserved';
  return null;
}

export function mitComercialMetadataValueError(
  type: MitComercialMetadataType,
  value: string | null | undefined
): 'required' | 'maxlength' | 'integer' | null {
  const text = type === 'NUMBER' ? (value ?? '').trim() : value ?? '';
  if (!text.trim()) return 'required';
  if (type === 'TEXT') return text.length > MAX_TEXT_LENGTH ? 'maxlength' : null;
  if (!/^-?\d+$/.test(text)) return 'integer';
  const number = Number(text);
  return number < INT32_MIN || number > INT32_MAX ? 'integer' : null;
}

/** Índices de las filas cuya llave (sin espacios a los lados) ya apareció antes. */
export function mitComercialMetadataDuplicates(keys: (string | null | undefined)[]): number[] {
  const seen = new Set<string>();
  const duplicates: number[] = [];
  keys.forEach((key, index) => {
    const value = (key ?? '').trim();
    if (!value) return;
    if (seen.has(value)) duplicates.push(index);
    seen.add(value);
  });
  return duplicates;
}

function rowValidator(group: AbstractControl): ValidationErrors | null {
  const value = group.value as { key: string; type: MitComercialMetadataType; value: string };
  const errors: ValidationErrors = {};
  const keyError = mitComercialMetadataKeyError(value.key);
  const valueError = mitComercialMetadataValueError(value.type, value.value);
  if (keyError) errors['key'] = keyError;
  if (valueError) errors['value'] = valueError;
  return Object.keys(errors).length ? errors : null;
}

function arrayValidator(array: AbstractControl): ValidationErrors | null {
  const rows = (array as MitComercialMetadataForm).getRawValue() as { key: string }[];
  const errors: ValidationErrors = {};
  if (rows.length > MIT_COMERCIAL_METADATA_MAX_KEYS) errors['tooMany'] = true;
  if (mitComercialMetadataDuplicates(rows.map((row) => row.key)).length) errors['duplicate'] = true;
  return Object.keys(errors).length ? errors : null;
}

export function buildMitComercialMetadataRow(fb: FormBuilder): FormGroup {
  return fb.group({
    key: fb.nonNullable.control(''),
    type: fb.nonNullable.control<MitComercialMetadataType>('TEXT'),
    value: fb.nonNullable.control('')
  }, { validators: rowValidator });
}

/** Sin filas; el campo es opcional. */
export function buildMitComercialMetadataForm(fb: FormBuilder): MitComercialMetadataForm {
  return fb.array<FormGroup>([], arrayValidator);
}

/** `undefined` sin filas; los valores de tipo Número se envían como números JSON. */
export function mitComercialMetadataFromForm(array: MitComercialMetadataForm): MitComercialMetadata | undefined {
  const rows = array.getRawValue() as { key: string; type: MitComercialMetadataType; value: string }[];
  if (rows.length === 0) return undefined;
  return Object.fromEntries(rows.map((row) => [row.key.trim(), row.type === 'NUMBER' ? Number(row.value.trim()) : row.value]));
}

const KEY_ERROR_TEXT: Record<string, string> = {
  required: 'Indica la llave.',
  maxlength: `Máximo ${MAX_KEY_LENGTH} caracteres.`,
  reserved: 'Llave reservada por Pento.'
};

const VALUE_ERROR_TEXT: Record<string, string> = {
  required: 'Indica el valor.',
  maxlength: `Máximo ${MAX_TEXT_LENGTH} caracteres.`,
  integer: 'Debe ser un número entero (usa Texto para identificadores largos).'
};

/** Filas llave / tipo / valor de la metadata comercial de un mandato. */
@Component({
  selector: 'app-mit-comercial-metadata-fields',
  templateUrl: './mit-comercial-metadata-fields.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ReactiveFormsModule, ButtonModule, FloatLabelModule, InputTextModule, SelectModule]
})
export class MitComercialMetadataFieldsComponent {
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  rows = input.required<MitComercialMetadataForm>();
  idPrefix = input('mit-comercial');

  readonly maxKeys = MIT_COMERCIAL_METADATA_MAX_KEYS;
  readonly types: { label: string; value: MitComercialMetadataType }[] = [
    { label: 'Texto', value: 'TEXT' },
    { label: 'Número', value: 'NUMBER' }
  ];

  constructor() {
    // El formulario padre marca las filas como tocadas al enviar sin pasar por este componente.
    effect((onCleanup) => {
      const subscription = this.rows().events.subscribe(() => this.cdr.markForCheck());
      onCleanup(() => subscription.unsubscribe());
    });
  }

  add(): void {
    if (this.rows().length >= MIT_COMERCIAL_METADATA_MAX_KEYS) return;
    this.rows().push(buildMitComercialMetadataRow(this.fb));
  }

  remove(index: number): void {
    this.rows().removeAt(index);
    this.rows().markAsDirty();
  }

  keyError(row: AbstractControl, index: number): string | null {
    if (!this.touched(row)) return null;
    const error = row.errors?.['key'] as string | undefined;
    if (error) return KEY_ERROR_TEXT[error];
    const keys = (this.rows().getRawValue() as { key: string }[]).map((value) => value.key);
    return mitComercialMetadataDuplicates(keys).includes(index) ? 'Llave repetida.' : null;
  }

  valueError(row: AbstractControl): string | null {
    if (!this.touched(row)) return null;
    const error = row.errors?.['value'] as string | undefined;
    return error ? VALUE_ERROR_TEXT[error] : null;
  }

  isNumber(row: AbstractControl): boolean {
    return row.get('type')?.value === 'NUMBER';
  }

  private touched(row: AbstractControl): boolean {
    return row.touched || row.dirty;
  }
}
